import { debounce } from 'lodash'
import { TypedEmitter } from 'tiny-typed-emitter'
import { logger } from '../services/Logger'

const log = logger.createChildLogger({ name: 'SpeakerDetectionManager' })

interface SpeakerDetectionManagerEvents {
    streamActivityChanged: (trackId: string, isActive: boolean) => void
    speakerActivityChanged: (isSpeaking: boolean) => void
}

type StreamData = {
    track: MediaStreamTrack
    source: MediaStreamAudioSourceNode | null
    analyzer: AnalyserNode | null
    isActive: boolean | null
    timeoutId?: NodeJS.Timeout
}

class SpeakerDetectionManager extends TypedEmitter<SpeakerDetectionManagerEvents> {
    private audioContext: AudioContext
    private streams: Map<string, StreamData> = new Map()
    private silenceThreshold: number = -50 // dB
    private detectionWindowMs: number = 500
    private isSpeakerActive: boolean | null = null
    private isDetectionActive: boolean = false

    constructor() {
        super()
        this.audioContext = new AudioContext()
        log.verbose('SpeakerDetectionManager', 'SpeakerDetectionManager initialized.')
    }

    registerTrack(track: MediaStreamTrack) {
        const trackId = track.id
        if (this.streams.has(trackId)) {
            log.verbose('registerTrack', `trackId="${trackId}" already registered; no-op.`)
            return
        }

        this.streams.set(trackId, {
            track,
            source: null,
            analyzer: null,
            isActive: null,
        })

        if (this.isDetectionActive) {
            log.verbose('registerTrack', `calling startSilenceDetection for trackId="${trackId}".`)
            this.startSilenceDetection(trackId)
        }

        // Monitor stream state and auto-unregister if stopped
        track.addEventListener('ended', () => {
            log.verbose('trackEndedEvent', `trackId="${trackId}" ended.`)
            this.unregisterTrack(track)
        })
    }

    unregisterTrack(track: MediaStreamTrack) {
        const trackId = track.id
        const streamData = this.streams.get(trackId)
        if (streamData) {
            log.verbose('unregisterTrack', `trackId="${trackId}" unregistering.`)
            streamData.timeoutId && clearInterval(streamData.timeoutId)
            streamData.source && streamData.source.disconnect()
            this.streams.delete(trackId)
        } else {
            log.verbose('unregisterTrack', `trackId="${trackId}" not found; no-op.`)
        }
    }

    startDetection() {
        if (this.isDetectionActive) {
            return
        }

        log.verbose('startDetection', 'starting speaker detection.')

        this.isDetectionActive = true

        this.streams.forEach((_, trackId) => {
            try {
                this.startSilenceDetection(trackId)
            } catch (e) {}
        })
    }

    getPerTrackSilenceState() {
        const s: { [trackId: string]: boolean } = {}
        this.streams.forEach((streamData, trackId) => {
            s[trackId] = streamData.isActive ?? false
        })
        return s
    }

    resetActiveState() {
        this.isSpeakerActive = null
        this.streams.forEach((streamData) => {
            streamData.isActive = null
        })
    }

    stopDetection() {
        log.verbose('stopDetection', 'stopping speaker detection.')
        this.isDetectionActive = false
        this.streams.forEach((streamData) => {
            this.unregisterTrack(streamData.track)
        })
    }

    private onStateChanged(trackId: string, isActive: boolean) {
        this.emit('streamActivityChanged', trackId, isActive)

        // If any one of the devices become active, then emit active.
        if (isActive && !this.isSpeakerActive) {
            this.isSpeakerActive = true
            log.verbose('onStateChanged', `isSpeakerActive=${this.isSpeakerActive}.`)
            this.emit('speakerActivityChanged', this.isSpeakerActive)
        }

        const speakerActivityChangedDebounced = debounce(() => {
            let hasActiveStream = false
            this.streams.forEach((streamData) => {
                if (streamData.isActive) {
                    hasActiveStream = true
                }
            })
            // `hasActiveStream` is true if any one of the tracks are active
            if (hasActiveStream === false && this.isSpeakerActive !== false) {
                this.isSpeakerActive = false
                log.verbose('onStateChanged', `isSpeakerActive=${this.isSpeakerActive}.`)
                this.emit('speakerActivityChanged', this.isSpeakerActive)
            }
        }, 1000)
        speakerActivityChangedDebounced()
    }

    private startSilenceDetection(trackId: string) {
        const streamData = this.streams.get(trackId)
        if (!streamData) return

        if (!streamData.analyzer) {
            const source = this.audioContext.createMediaStreamSource(new MediaStream([streamData.track]))
            const analyzer = this.audioContext.createAnalyser()
            analyzer.fftSize = 2048
            source.connect(analyzer)

            streamData.source = source
            streamData.analyzer = analyzer
        }

        log.verbose(
            'startSilenceDetection',
            `starting detection for trackId="${trackId}". audioContext.state="${this.audioContext.state}".`
        )
        if (this.audioContext.state === 'suspended') {
            try {
                this.audioContext.resume() // necessary for browser
            } catch (e) {
                log.error('startSilenceDetection', `error resuming audio context.`, { e })
            }
        }

        const dataArray = new Float32Array(streamData.analyzer.frequencyBinCount)

        const detectSilence = () => {
            if (!streamData.analyzer) return

            streamData.analyzer.getFloatTimeDomainData(dataArray)

            // Calculate RMS value
            let sum = 0
            for (let i = 0; i < dataArray.length; i++) {
                sum += dataArray[i] * dataArray[i]
            }
            const rms = Math.sqrt(sum / dataArray.length)
            const db = 20 * Math.log10(rms)

            // Check if audio level is below threshold
            const currentlyIsActive = db > this.silenceThreshold

            log.isSilly() &&
                log.silly(
                    'detectSilence',
                    `sum=${sum}, dataArray.length=${dataArray.length}, db=${db}, currentlyIsActive=${currentlyIsActive}.`
                )

            if (currentlyIsActive !== streamData.isActive) {
                streamData.isActive = currentlyIsActive
                this.onStateChanged(trackId, currentlyIsActive)
            }
        }

        streamData.timeoutId = setInterval(detectSilence, this.detectionWindowMs)

        detectSilence()
    }
}

export const speakerDetectionManager = new SpeakerDetectionManager()
